// lib/orders/book-status-guard.ts
//
// Book status writes for the checkout -> process-purchase hand-off.

/**
 * A paid book whose generation could not be started must leave a visible state, not just a log line.
 * PROCESSING_ERROR is the status process-purchase itself uses for failed generation; the stuck-paid-books sweep
 * (services/stuck-paid-books.ts) retries these books. Writes status only - never additional_comments.
 */
export async function markBookGenerationFailed(supabase: any, bookId: string, reason: string): Promise<void> {
  try {
    const { error } = await supabase.from('books').update({ status: 'PROCESSING_ERROR' }).eq('id', bookId);
    if (error) throw error;
    console.error(`❌ [CHECKOUT-COMPLETE] Book ${bookId} marked PROCESSING_ERROR: ${reason}`);
  } catch (e) {
    console.error(`❌ [CHECKOUT-COMPLETE] Could not mark book ${bookId} as failed (${reason}):`, e);
  }
}

/**
 * Mark the book PROCESSING after the payment checks, but never over PROCESSING_ERROR: process-purchase is fired
 * earlier, and a fast failure of it must not be overwritten by this later write.
 */
export async function markBookProcessingUnlessFailed(supabase: any, bookId: string): Promise<void> {
  const { error } = await supabase
    .from('books')
    .update({ status: 'PROCESSING', updated_at: new Date().toISOString() })
    .eq('id', bookId)
    .neq('status', 'PROCESSING_ERROR');
  if (error) throw error;
}

/**
 * Book statuses a book can have while it is waiting to be bought (preview/story ready, before checkout/complete ran).
 * checkout/complete's first run always moves a book out of these (PROCESSING, or PROCESSING_ERROR when
 * process-purchase fails fast) before it sends the confirmation email. Used by the Stripe webhook's completion
 * backstop to refuse a second run when the Redis completion record is missing. Unknown statuses count as "not
 * waiting" (refuse): a missed backstop only loses a retry, a wrong one sends a second email and generation.
 */
export const PRE_PURCHASE_BOOK_STATUSES = [
  'DRAFT',
  'STORY_TEXT_READY',
  'STORY_READY',
  'STORY_READY_COVER_FIRST',
  'MULTI_STORY_READY',
  'MULTI_BOOK_COVER_PENDING',
  'MULTI_BOOK_READY',
  'PREVIEW_READY',
] as const;

export function bookAwaitingPurchase(status: string | null | undefined): boolean {
  return !!status && (PRE_PURCHASE_BOOK_STATUSES as readonly string[]).includes(status);
}
